import {common} from '@priority-software/priority-proto-api/lib';
import {
    getServerStoreData,
    setServerStoreData
} from '../../../services/proto/ServerStorage/service';
import {Form} from '../../../stores/Form/Form';
import {
    getDname,
    getUsername,
    startProcWithActivationSource
} from '../../../_shared/GWTBridge/invoke/genPage';
import {isDevEnv} from '../../../utils/platform';
import {getUserFullName} from './user';
import {observable} from 'mobx';
import {ActivationSource} from '../../../constants/ActivationSource';

export const eoyResponsibleKey = `eoy:responsible`;
export const eoyOwnerKey = `eoy:owner`;
export const eoyCompletedStepUserKey = 'eoy:completed_step_user';
export const eoyStepAssignByKey = 'eoy:step_assigned_by';
export const eoyStepAssignedByFullName = 'eoy:step_assigned_by_full_name';
export const eoyRemindLaterDateKey = 'eoy:remind_later_date';

export const EOYReminderStore = observable({
    showReminder: false
});

export const showEOYReminder = (flag: boolean) => {
    EOYReminderStore.showReminder = flag;
};

export const setEoyResponsible = async (eoyResponsible: string, isOwner: boolean = false) => {
    if (isOwner) {
        await saveStringDataToServerStore(eoyOwnerKey, eoyResponsible);
    }
    await saveStringDataToServerStore(eoyResponsibleKey, eoyResponsible);
};

export const getEoyResponsible = async () => {
    return await getServerStoreData([eoyResponsibleKey, eoyOwnerKey], true, false);
};

export const getEoyStoredData = async () => {
    return await getServerStoreData(['eoy:*'], true, false);
};

export const saveStringDataToServerStore = async (key: string, value: string) => {
    const storeData = {[key]: new common.Constant({Str: value})};

    await setServerStoreData(storeData, false, true);
};

export const saveCompletedStepUser = async (stepID: string, owner: string) => {
    if (stepID) {
        const fullName = await getUserFullName(getUsername() || '');

        const storeData = {
            [eoyCompletedStepUserKey]: new common.Constant({
                Str: `${stepID}:${getUsername()}:${fullName}`
            }),
            [eoyResponsibleKey]: new common.Constant({Str: owner}) // Set EOY owner as a responsible user for the next step
        };

        await setServerStoreData(storeData, false, true);
    }
};

export const saveEoyStepAssignedBy = async (user: string) => {
    await saveStringDataToServerStore(eoyStepAssignByKey, user);
    const fullName = await getUserFullName(getUsername() || '');
    await saveStringDataToServerStore(eoyStepAssignedByFullName, fullName);
};

export const clearStepCompletedBy = async () => {
    await saveStringDataToServerStore(eoyCompletedStepUserKey, '');
};

export const clearStepAssignBy = async () => {
    await saveStringDataToServerStore(eoyStepAssignByKey, '');
};

export const clearPrevCompletedStepUser = async () => {
    await saveStringDataToServerStore(eoyCompletedStepUserKey, '');
};

const getReminderKey = (stepID: string) => {
    return `${getDname()}:${stepID}:${eoyRemindLaterDateKey}`;
};
export const setReminder = async (stepID?: string) => {
    if (stepID) {
        const reminderDate = getCurrentDateForEOYReminders();

        reminderDate.setDate(reminderDate.getDate() + 1);
        reminderDate.setHours(0, 0, 0, 0);

        localStorage.setItem(getReminderKey(stepID), reminderDate.toISOString());
    }
};

export const getReminderPostponedDate = (stepID: string) => {
    return localStorage.getItem(getReminderKey(stepID));
};

export const clearReminder = (stepID: string) => {
    return localStorage.removeItem(getReminderKey(stepID));
};

export const runWizardStepFromReminder = async () => {
    startProcWithActivationSource('EOYWIZARD', ActivationSource.EOYWizard);
};

export const getEoyFiscalYear = async (form: Form) => {
    const record = form.records[0];

    const newStartDate = record?.getField(form.getColumnByName('SDATE')?.id)?.Value?.Date;
    const newEndDate = record?.getField(form.getColumnByName('EDATE')?.id)?.Value?.Date;
    const currentEndDate = record?.getField(form.getColumnByName('EDATEOLD')?.id)?.Value?.Date;

    return {newStartDate, currentEndDate, newEndDate};
};

export const getCurrentDateForEOYReminders = () => {
    let currentDate = new Date();

    if (isDevEnv()) {
        const params = new URLSearchParams(window.location.search);
        // Test dates should be in format of 'YYYY-MM-DD' => '2024-08-14
        const testParams = params.get('_eoyQADate');
        const testDate = new Date(testParams);

        if (testParams && !isNaN(testDate.getTime())) {
            currentDate = new Date(testDate);
            console.log('Using the test date from query params for EOY reminders:', currentDate);
        } else {
            console.error('Invalid test dates for EOY reminders:', currentDate);
        }
    }
    return currentDate;
};

const clearLocalStorageReminderKeys = () => {
    // Clear all EOY reminders related keys from local storage
    const clearLocalStorageKeys = Object.keys(localStorage).filter(
        key => key.includes(eoyRemindLaterDateKey) && key.includes(getDname())
    );
    clearLocalStorageKeys.forEach(key => localStorage.removeItem(key));
};

export const clearEOYRemindersStoreData = async (ownerName?: string) => {
    clearLocalStorageReminderKeys();

    // Clear all EOY reminders related keys from server store
    const storeData = {
        [eoyResponsibleKey]: new common.Constant({Str: ''}),
        [eoyCompletedStepUserKey]: new common.Constant({Str: ''}),
        [eoyStepAssignByKey]: new common.Constant({Str: ''}),
        [eoyStepAssignedByFullName]: new common.Constant({Str: ''})
    };

    if (ownerName) {
        storeData[eoyResponsibleKey] = new common.Constant({Str: ownerName});
    }
    await setServerStoreData(storeData, false, true);
};
